import { useGetViewByTypeQuery } from 'store/services/flow/views';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content, ContentWrapper, MainContainer } from './Layout.styles';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Menu } from './Menu';
import { Header } from './Header';
import { Breadcrumbs } from './Breadcrumbs';
import { DrawerWrapper } from './Drawer/DrawerWrapper';
import { useLogin, useStore } from 'contexts';
import { isMobileApp } from 'utils/common';
import { selectDrawer } from 'store/entities/drawer/selectors';
import { selectMenuOpened } from 'store/entities/dashboard/selectors';

import { VIEW_TYPE, VISIBILITY_STATE } from 'types/enums';
import { useRecordDetailScreen } from 'utils/hooks';
import { uiStateActions } from 'store/entities/ui';

import { PostHogProvider } from '@posthog/react';
import { posthog } from 'utils/posthog/posthogClient';
import { PostHogListener } from 'utils/posthog/PostHogListener';
import { getPostLoginRedirectPath } from 'utils/postLoginRedirect';
import { isExternalRestrictedPath } from 'utils/auth/userType';
import { STORE_KEYS } from 'types/store';

const POST_LOGIN_HOME_PATHS = new Set(['/user', '/admin']);

const LayoutBase = () => {
  const mobileApp = isMobileApp();
  const { user, isExternal } = useLogin();
  const { getItem: getStoreItem, clearItem: clearStoreItem } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isDetailScreen = useRecordDetailScreen();
  const open = useSelector(selectMenuOpened());
  const drawer = useSelector(selectDrawer());
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const { id: moduleId } = useParams<{ id: string }>();
  const { data: { views: shoppingCarts } = {} } = useGetViewByTypeQuery(
    { viewType: VIEW_TYPE.SHOPPING_CART },
    {
      skip: !moduleId || isExternal,
    }
  );

  const visibleShoppingCarts = useMemo(() => {
    return shoppingCarts?.filter((cart) => cart.visibilityState === VISIBILITY_STATE.VISIBLE) || [];
  }, [shoppingCarts]);
  const hideHeader = isDetailScreen && mobileApp;
  const isAgendaListPage = /\/user\/modules\/[^/]+(\/m\/[^/]+)?\/(Helios|Flow)\/[^/]+(\/view\/[^/]+)?$/.test(location.pathname);
  const isModuleDetailPage = /\/user\/modules\/[^/]+(\/m\/[^/]+)?$/.test(location.pathname) && !isAgendaListPage;
  const isRecordDetailPage = /\/user\/modules\/[^/]+(\/m\/[^/]+)?\/(Helios|Flow)\/[^/]+\/r\/[^/]+\/detail/.test(location.pathname);
  const isAdminRoute = location.pathname.includes('/admin');
  const hideBreadcrumbs = isAgendaListPage || isModuleDetailPage || isAdminRoute || isRecordDetailPage || isDetailScreen || isExternal;

  useEffect(() => {
    if (ref.current) {
      const styles = window.getComputedStyle(ref.current);

      const paddingTop = parseFloat(styles.paddingTop);
      const paddingBottom = parseFloat(styles.paddingBottom);

      dispatch(uiStateActions.setComponentPadding({ id: 'layout-content', padding: { top: paddingTop, bottom: paddingBottom } }));
    }
  }, [dispatch]);

  useEffect(() => {
    const canRestoreInternalRedirect = !isExternal && POST_LOGIN_HOME_PATHS.has(location.pathname);
    const canRestoreExternalRedirect = isExternal && location.pathname === '/shared';

    if (!user || (!canRestoreInternalRedirect && !canRestoreExternalRedirect)) {
      return;
    }

    const redirect = getStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT);
    const redirectPath = getPostLoginRedirectPath(redirect, window.location.origin);

    clearStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT);

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [clearStoreItem, getStoreItem, isExternal, location.pathname, navigate, user]);

  if (user && isExternal && isExternalRestrictedPath(location.pathname)) {
    return <Navigate to="/shared" replace />;
  }

  return (
    <PostHogProvider client={posthog}>
      <PostHogListener />
      {user ? (
        <MainContainer>
          <Helmet titleTemplate="%s | Flowio" defaultTitle="Flowio" />
          {!isExternal && <Menu />}
          <ContentWrapper isDrawerOpen={drawer?.isOpen} isOnMobileApp={mobileApp}>
            {!hideHeader && <Header shoppingCarts={visibleShoppingCarts} />}
            <Breadcrumbs hidden={hideBreadcrumbs} />
            <Content open={open} ref={ref} isOnMobileApp={mobileApp} isDetailScreen={isDetailScreen} isAdminRoute={isAdminRoute} hideMenu={isExternal}>
              <Outlet />
            </Content>
          </ContentWrapper>
          {drawer?.isOpen && <DrawerWrapper />}
        </MainContainer>
      ) : (
        <>
          <Outlet />
        </>
      )}
    </PostHogProvider>
  );
};

export const Layout = memo(LayoutBase);
